import {
  FieldAttributeFragment,
  FieldOptionFragment,
} from '@formie/gql/_generated/fieldCommon.generated';
import { FormieFieldFragment } from '@formie/gql/_generated/fields.generated';
import { FormieFormFragment } from '@formie/gql/_generated/form.generated';
import { parseFormieFieldCondition } from '@formie/fieldConditions';
import { makeNonNullableArray, toString } from '@liquorice/allsorts-craftcms-nextjs';

import {
  createPropSanitiser,
  deleteUndefined,
  firstNonNullable,
  SanitiserTypes,
  toBoolean,
  Typename,
} from '@liquorice/allsorts-craftcms-nextjs';

type RawElements = FormieFieldFragment;

/**
 * __typename of top level Elements
 */
type FieldTypename = Typename<RawElements>;

// ----------------------------------------------------------------------------------------------------
// ---- Label Options ----

export type LabelPosition = 'default' | 'hidden';

export const parseLabelPosition = (data: Maybe<string>): LabelPosition => {
  if (!data) return 'default';

  const type = toString(data).split('\\').reverse()[0].toLowerCase();

  if (type === 'hidden') return type;

  return 'default';
};
// ----------------------------------------------------------------------------------------------------
// ---- Field Options ----

export type FieldOption = {
  value: string;
  label: string;
};

export const parseFieldOptions = (
  data: MaybeArrayOf<FieldOptionFragment | FieldAttributeFragment>
): FieldOption[] => {
  return makeNonNullableArray(data).map(({ value, label }) => ({
    value: toString(value),
    label: toString(label),
  }));
};

// ----------------------------------------------------------------------------------------------------
// ---- Nested fields (Formie 3's Field_Name/Field_Address sub-fields, e.g. Field_NameFirst) ----

export type NestedFieldFragment = {
  __typename: string;
  handle?: string | null;
  label?: string | null;
  enabled?: boolean | null;
  required?: boolean | null;
  placeholder?: string | null;
  defaultValue?: string | null;
  errorMessage?: string | null;
  options?: MaybeArrayOf<FieldOptionFragment> | null;
};

export type NestedField = {
  __typename: string;
  handle: string;
  label: string;
  enabled: boolean;
  required: boolean;
  placeholder?: string;
  defaultValue?: string;
  errorMessage?: string;
  options?: FieldOption[];
};

export const parseNestedFields = (data: MaybeArrayOf<NestedFieldFragment>): NestedField[] => {
  return makeNonNullableArray(data).map((f) => ({
    __typename: f.__typename,
    handle: toString(f.handle),
    label: toString(f.label),
    enabled: toBoolean(f.enabled),
    required: toBoolean(f.required),
    placeholder: f.placeholder ? toString(f.placeholder) : undefined,
    defaultValue: f.defaultValue ? toString(f.defaultValue) : undefined,
    errorMessage: f.errorMessage ? toString(f.errorMessage) : undefined,
    options: f.options ? parseFieldOptions(f.options) : undefined,
  }));
};

/**
 * Finds a Field_Name/Field_Address sub-field by its `__typename`
 * (e.g. `Field_NameFirst`, `Field_AddressCity`) rather than by `handle`,
 * since `__typename` is guaranteed by the schema.
 */
export const findNestedField = (fields: NestedField[] | undefined, typename: string) =>
  fields?.find((f) => f.__typename === typename);

// ----------------------------------------------------------------------------------------------------
// --- Define the callbacks ---

export const sanitiseFormieField = createPropSanitiser((): RawElements | null => null, {
  errorMessage: toString,
  instructions: toString,
  handle: toString,
  label: toString,
  placeholder: toString,
  required: toBoolean,
  options: parseFieldOptions,
  multi: toBoolean,
  conditions: parseFormieFieldCondition,
  enableConditions: toBoolean,
  labelPosition: parseLabelPosition,
  //Agree
  defaultState: toBoolean,
  checkedValue: toString,
  uncheckedValue: toString,
  //
  // Address/Name (Formie 3's Field_Name/Field_Address expose their sub-fields as a nested
  // `fields` list, e.g. Field_NameFirst/Field_AddressCity, rather than flattened properties)
  fields: parseNestedFields,
});

// ----------------------------------------------------------------------------------------------------
// --- Extracted sanitised types ---

type SanitiserReturnMap = SanitiserTypes<typeof sanitiseFormieField, 'ReturnMap'>;

export type SanitisedField<T extends FieldTypename = FieldTypename> = SanitiserReturnMap[T];
export type Field<T extends FieldTypename = FieldTypename> = SanitisedField<T> & {
  _fieldMeta?: FieldMeta;
  handle: string;
};

export type FieldMeta = {
  index: number;
  first: boolean;
  firstOfType: boolean;
  nthOfType: number;
  last: boolean;
  previousField?: Field;
};

// ----------------------------------------------------------------------------------------------------

export type FieldComponentProps<T extends FieldTypename, P = NoProps> = {
  field: Field<T>;
  enabled?: boolean;
} & P;

/**
 * Create a {@link Field} consumer React Component by providing
 * the `__typename` of `FieldType` as T
 */
export const createField = <T extends FieldTypename = FieldTypename, P = NoProps, R = JSX.Element>(
  fn: (props: FieldComponentProps<T, P>) => R
) => fn;

export const sanitiseFields = (maybeFields: MaybeArrayOf<RawElements>) => {
  return sanitiseFormieField.many(maybeFields) as SanitisedField[];
};

export const parseSanitisedFields = (sanitisedFields: SanitisedField[]): Field[] => {
  const nthOfTypeCount: Record<string, number> = {};

  return sanitisedFields.map((v, i): Field => {
    const type = v.__typename;

    nthOfTypeCount[type] = type in nthOfTypeCount ? nthOfTypeCount[type] + 1 : 0;
    return deleteUndefined({
      ...v,
      _fieldMeta: {
        nthOfType: nthOfTypeCount[type],
        index: i,
        first: i === 0,
        firstOfType: !!sanitisedFields.find((x, j) => j < i && x.__typename === v.__typename),
        last: i === sanitisedFields.length - 1,
        previousField: sanitisedFields[i - 1],
      },
    });
  });
};

export const parseFields = (maybeFields: MaybeArrayOf<RawElements>): Field[] => {
  return parseSanitisedFields(sanitiseFields(maybeFields));
};

// ----------------------------------------------------------------------------------------------------
// --- Type guards ---

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const isFieldType = <T extends FieldTypename>(x: any, typename: T): x is Field<T> => {
  return !!x && x.__typename === typename;
};

// ----------------------------------------------------------------------------------------------------
// ---- Form ----

export const parseFormieForm = (data: MaybeArrayOf<FormieFormFragment>) => {
  const form = firstNonNullable(data);

  if (!form) return null;

  const { formFields = [], ...rest } = form;

  return {
    ...rest,
    formFields: parseFields(formFields),
  };
};

export type ParsedForm = NonNullable<ReturnType<typeof parseFormieForm>>;
